import { useMutation, useQueryClient } from 'react-query';
import { updatePickupKpi, PickupKpiPayload } from 'api/pickupKpi';
import { successToast } from 'utils/successToast';
import { errorToast } from 'utils/errorToast';
import { toApiPayloadDateFormat } from 'utils/dateUtils';

export const useUpdatePickupKpi = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: PickupKpiPayload & { _id: string }) => {
      return await updatePickupKpi(payload);
    },
    {
      onSuccess: (data, payload) => {
        // Optimistically update the cache
        queryClient.setQueryData(
          [`fetch-pickups-ById`, payload._id],
          (oldData: any) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              employees: payload.employees,
              processTime: payload.processTime,
              countingDate: payload.countingDate,
            };
          },
        );

        // Invalidate related queries to sync with server
        queryClient.invalidateQueries([`fetch-pickups-ById`, payload._id]);
        queryClient.invalidateQueries(['fetch-pickups']);

        successToast('Pickup KPI data updated successfully');
      },
      onError: (error) => {
        console.error('Error updating pickup KPI:', error);
        errorToast('Failed to update pickup KPI data');
      },
    },
  );
};

// Hook with automatic date formatting for easier form integration
export const usePickupKpiWithDateFormat = () => {
  const updateMutation = useUpdatePickupKpi();

  const updateWithDateFormat = async (
    pickupId: string,
    values: {
      countingDate: Date | string;
      employees: number;
      processTime: { hours: number; minutes: number };
    },
  ) => {
    // Use the existing utility function that handles UTC conversion properly
    const formattedDate = toApiPayloadDateFormat(values.countingDate);

    const payload: PickupKpiPayload & { _id: string } = {
      _id: pickupId,
      countingDate: formattedDate,
      employees: parseInt(values.employees.toString()) || 0,
      processTime: {
        hours: parseInt(values.processTime?.hours.toString()) || 0,
        minutes: parseInt(values.processTime?.minutes.toString()) || 0,
      },
    };

    return updateMutation.mutateAsync(payload);
  };

  return {
    ...updateMutation,
    updateWithDateFormat,
  };
};
